import React, { Fragment, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useEmailValidation } from 'hooks/accounts/useEmailValidation'
import { toast } from 'react-toastify'
import { Image, Icon, Button, List, Header, Segment, Modal } from 'semantic-ui-react'
import { Link } from 'react-router'
import { PaymentPreferencesLabel } from 'components/labels'
import { CopyToClipboardButton } from 'components/buttons'
import { EditEmailPopupForm } from 'components/forms/users'
import ApiErrorMessage from 'components/errors/ApiErrorMessage'
import { scaleCropFileUrl } from 'helpers/files'
import { getLocalityAndCountry } from 'helpers/places'
import './UserCard.css'

function AccountStatus({ accountId, email, isValidated }) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const handleClose = useCallback(() => {
    setIsOpen(false)
    toast.success(`${t("ToastNotification::User's account has been been validated!")} 👍`, { type: toast.TYPE.SUCCESS })
  }, [t])
  const { isValidating, validateError, validateAccount } = useEmailValidation({
    accountId,
    onValidation: handleClose,
    newEmail: email,
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
      <Icon
        circular
        inverted
        size='small'
        name={isValidated ? 'check' : 'warning sign'}
        color={isValidated ? 'green' : 'yellow'}
      />
      {isValidated ? t('UserCard::Account validated') : t('UserCard::Account not validated')}
      {!isValidated && (
        <Modal
          basic
          onClose={handleClose}
          onOpen={() => setIsOpen(true)}
          open={isOpen}
          size='small'
          trigger={<Icon name='cog' size='large' color='grey' style={{ cursor: 'pointer' }} />}
        >
          <Header icon>
            <Icon name='handshake' />
            {t('UserCard::Validate account')}
          </Header>
          <Modal.Content>
            <p>
              {t('UserCard::Are you sure you want to validate the user account? This operation is not reversible.')}
            </p>
            <ApiErrorMessage error={validateError} noStack />
          </Modal.Content>
          <Modal.Actions>
            <Button basic color='red' inverted onClick={handleClose}>
              <Icon name='remove' /> No
            </Button>
            <Button color='green' inverted onClick={validateAccount} loading={isValidating}>
              <Icon name='checkmark' /> Yes
            </Button>
          </Modal.Actions>
        </Modal>
      )}
    </div>
  )
}

AccountStatus.propTypes = {
  accountId: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  isValidated: PropTypes.bool.isRequired,
}

export const UserCard = ({ user, place, brandName, connectAs, showUserDetails, children }) => {
  const { t } = useTranslation()
  if (!user) return null

  return (
    <Segment>
      <div style={{ textAlign: 'center' }}>
        {user.avatar && <Image circular centered size='small' src={scaleCropFileUrl(user.avatar, '500x500')} />}
        <Header size='medium'>
          {`${user.firstname} ${user.lastname}`}
          {brandName && <Header.Subheader>{brandName}</Header.Subheader>}
        </Header>
        <AccountStatus
          accountId={user.account.id}
          email={user.account.email}
          isValidated={!!user.account.validated_at}
        />
        <Segment basic>
          {connectAs && (
            <Button primary basic compact onClick={connectAs}>
              {t('UserCard::Connect')}
            </Button>
          )}
          <Button as={Link} primary basic compact to={{ pathname: '/users', query: { search: user.id } }}>
            {t('UserCard::View in list')}
          </Button>
        </Segment>
      </div>

      <List>
        {user.account.email && (
          <List.Item style={{ display: 'flex', alignItems: 'center' }}>
            <Icon name='mail outline' />
            <List.Content style={{ width: 'auto', maxWidth: '80%', overflowWrap: 'break-word' }}>
              {user.account.email}
            </List.Content>
            <CopyToClipboardButton
              text={user.account.email}
              tooltipClassName='__userCardTooltip'
              style={{ position: 'static', marginLeft: 4 }}
            />
            <EditEmailPopupForm account={user.account} />
          </List.Item>
        )}
        {user.phone && (
          <List.Item style={{ display: 'flex', alignItems: 'center' }}>
            <Icon name='phone' />
            <List.Content style={{ width: 'auto' }} verticalAlign='middle'>
              {user.phone}
            </List.Content>
            <CopyToClipboardButton
              text={user.phone}
              tooltipClassName='__userCardTooltip'
              style={{ position: 'static', marginLeft: 4 }}
            />
          </List.Item>
        )}
        {(place || user.locality) && (
          <List.Item>
            <Icon name='point' />
            <List.Content verticalAlign='middle'>
              {getLocalityAndCountry(place) || `${user.locality}, ${user.country}`}
            </List.Content>
          </List.Item>
        )}

        {showUserDetails && (
          <Fragment>
            {user.created_at && (
              <List.Item>
                <Icon name='child' />
                <List.Content verticalAlign='middle'>
                  {t('UserCard::Joined on {{date}}', { date: moment(user.created_at).format('ll') })}
                </List.Content>
              </List.Item>
            )}
            {user.account.facebook_id && (
              <List.Item>
                <Icon name='facebook f' />
                <List.Content verticalAlign='middle'>{t('UserCard::Registered with Facebook')}</List.Content>
              </List.Item>
            )}
            {user.account.google_id && (
              <List.Item>
                <Icon name='google' />
                <List.Content verticalAlign='middle'>{t('UserCard::Registered with Google')}</List.Content>
              </List.Item>
            )}

            {user.isHost && (
              <List.Item>
                <Icon name='dollar' />
                <List.Content verticalAlign='middle'>
                  <PaymentPreferencesLabel user={user} />
                </List.Content>
              </List.Item>
            )}
          </Fragment>
        )}
        {user.isHost && (
          <Segment basic textAlign='center'>
            <Button as={Link} primary basic compact to={{ pathname: '/events', query: { host: user.id } }}>
              {t('UserCard::View experiences in list')}
            </Button>
          </Segment>
        )}
      </List>

      {children}
    </Segment>
  )
}

UserCard.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    account: PropTypes.object,
    phone: PropTypes.string,
    created_at: PropTypes.string,
    locality: PropTypes.string,
    country: PropTypes.string,
    avatar: PropTypes.object,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    isHost: PropTypes.bool,
  }).isRequired,
  place: PropTypes.shape({
    id: PropTypes.number,
  }),
  connectAs: PropTypes.func,
  children: PropTypes.node,
  brandName: PropTypes.string,
  showUserDetails: PropTypes.bool,
}

UserCard.defaultProps = {
  children: undefined,
  showUserDetails: true,
  brandName: undefined,
  connectAs: undefined,
  place: undefined,
}
